/* ==========================================================================
   BEN'SHOME CART

   1. Upgrades every /cart/ link the size finder renders into real buy controls.
   2. Runs the slide-out cart, so adding never throws the customer off the page.
   3. Keeps the size visible after the add. The cart is the last place to catch
      a wrong one.
   4. Keeps delivery protection in step with the cart. See RECONCILE.
   5. Puts the finder's answer straight into the cart. See AUTO-ADD.

   Items first in the drawer, footer short, one action in it. The long version
   of why is in benshome-cart.css.

   No dependencies. No app. Vanilla fetch against Shopify's own cart routes.
   ========================================================================== */

(function(){
  'use strict';

  var busy = false;

  function money(cents){ return '$' + (cents / 100).toFixed(2); }

  /* Titles are typed by a human in the admin and go into innerHTML. */
  function esc(s){
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  function el(id){ return document.getElementById(id); }

  /* ---------- delivery protection ----------
     Printed by cart-drawer.liquid from the product itself, so the price on the
     tickbox is the price the checkout charges. Missing block means PROT is null
     and every branch below is a no-op: an add-on that half works is worse than
     one that is switched off. */
  var PROT = (function(){
    var n = el('bh-protect');
    if(!n) return null;
    try { var d = JSON.parse(n.textContent); return (d && d.id) ? d : null; }
    catch(e){ console.warn("[BEN'SHOME] could not read #bh-protect", e); return null; }
  })();

  /* Session, not local: somebody who declined protection in March should be
     asked again in June, not opted out forever by a forgotten decision. */
  var OPTOUT = 'bhProtectOff';
  function optedOut(){
    try { return sessionStorage.getItem(OPTOUT) === '1'; } catch(e){ return false; }
  }
  function setOptOut(off){
    try { off ? sessionStorage.setItem(OPTOUT,'1') : sessionStorage.removeItem(OPTOUT); } catch(e){}
  }

  function isProt(item){ return !!PROT && Number(item.variant_id) === Number(PROT.id); }
  function realItems(cart){ return cart.items.filter(function(i){ return !isProt(i); }); }
  function protLine(cart){
    for(var i=0;i<cart.items.length;i++){ if(isProt(cart.items[i])) return cart.items[i]; }
    return null;
  }
  function lineFor(cart, variantId){
    for(var i=0;i<cart.items.length;i++){
      if(Number(cart.items[i].variant_id) === Number(variantId)) return cart.items[i];
    }
    return null;
  }
  function boardCount(cart){
    return realItems(cart).reduce(function(n, i){ return n + i.quantity; }, 0);
  }
  function onCartPage(){ return /^\/cart\/?$/.test(location.pathname); }

  /* ---------- Shopify cart routes ---------- */

  function getCart(){
    return fetch('/cart.js', {headers:{'Accept':'application/json'}})
      .then(function(r){ return r.json(); });
  }

  function addMany(list){
    return fetch('/cart/add.js', {
      method:'POST',
      headers:{'Content-Type':'application/json','Accept':'application/json'},
      body: JSON.stringify({items: list.map(function(v){ return {id:Number(v.id), quantity:v.qty}; })})
    }).then(function(r){
      if(!r.ok) return r.json().then(function(e){ throw new Error(e.description || e.message || 'Could not add that size.'); });
      return r.json();
    });
  }

  function changeLine(key, qty){
    return fetch('/cart/change.js', {
      method:'POST',
      headers:{'Content-Type':'application/json','Accept':'application/json'},
      body: JSON.stringify({id: key, quantity: qty})
    }).then(function(r){ return r.json(); });
  }

  /* PRESENT, not ADDED. Buy it now means "take me to checkout with this", and
     pressing it twice, or pressing it on a size the finder already put in the
     cart, must not buy two of them. Add to cart keeps plain addMany, because
     pressing that twice genuinely does mean two. */
  function ensure(list){
    return getCart().then(function(cart){
      var missing = list.filter(function(v){ return !lineFor(cart, v.id); });
      if(!missing.length) return cart;
      return addMany(missing).then(getCart);
    });
  }

  /* ---------- RECONCILE ----------
     One function owns whether protection is in the cart, and it runs on every
     read. In order: no boards, so any protection line goes; boards but no
     protection, so one is added unless the customer took it off this visit;
     never more than one, whatever the board count; otherwise leave alone.

     A failed protection add falls back to the cart as it was. The customer
     still gets their boards, and a broken add-on never blocks a sale.

     On /cart the page is Liquid-rendered, so a line added after that render
     would be invisible. Hence the reload, which cannot loop: once protection
     is present the rule that fires is "leave alone". */
  function reconcile(cart){
    if(!PROT) return Promise.resolve(cart);

    var boards = realItems(cart).length;
    var line   = protLine(cart);

    if(!boards){
      if(!line) return Promise.resolve(cart);
      return changeLine(line.key, 0).then(function(c){
        if(onCartPage()) location.reload();
        return c;
      });
    }

    if(!line){
      if(optedOut()) return Promise.resolve(cart);
      return addMany([{id: PROT.id, qty: 1}])
        .then(function(){ if(onCartPage()) location.reload(); return getCart(); })
        .catch(function(){ return cart; });
    }

    if(line.quantity !== 1) return changeLine(line.key, 1);

    return Promise.resolve(cart);
  }

  /* ---------- AUTO-ADD FROM THE FINDER ----------

     Picking a depth in the size finder is the moment the customer has answered
     the only question the product has, so the answer goes into the cart there
     and then rather than waiting behind one more button.

     CHOOSING AGAIN REPLACES, IT DOES NOT STACK. Somebody weighing 23" against
     25" presses both, and a cart that quietly kept the first one would send
     them to checkout with two boards and one sofa. lastAuto remembers only the
     line this flow put there, so going back and choosing differently swaps it
     and anything the customer added deliberately is left alone.

     The drawer is NOT opened. They are reading the result card they just asked
     for; sliding a panel over it would take away the thing they came for. The
     cart badge and the corner button carry the news instead. */
  var lastAuto = null;

  function autoAdd(variantId){
    if(busy) return;
    if(lastAuto && Number(lastAuto.id) === Number(variantId)) return;
    busy = true;

    var drop = lastAuto ? changeLine(lastAuto.key, 0) : Promise.resolve(null);

    drop.then(function(){ return addMany([{id: variantId, qty: 1}]); })
      .then(getCart)
      .then(reconcile)
      .then(function(cart){
        var line = lineFor(cart, variantId);
        lastAuto = line ? {id: variantId, key: line.key} : null;
        renderCart(cart);
        busy = false;
      })
      .catch(function(){ busy = false; });
  }

  /* ---------- drawer ---------- */

  /* bh-cart-open hides the corner dock, the mobile sticky bar, the cart button
     and the help widget while the panel is up. All four are position:fixed and
     were sitting on top of the scrim. Rule lives in benshome-cart.css. */
  function openDrawer(){
    var d = el('bh-cart');
    if(!d) return;
    d.classList.add('on');
    d.setAttribute('aria-hidden','false');
    document.body.classList.add('bh-noscroll','bh-cart-open');
    var close = d.querySelector('.bhc-close');
    if(close) close.focus();
  }

  function closeDrawer(){
    var d = el('bh-cart');
    if(!d) return;
    d.classList.remove('on');
    d.setAttribute('aria-hidden','true');
    document.body.classList.remove('bh-noscroll','bh-cart-open');
  }

  function sizeLine(item){
    var t = item.variant_title || '';
    if(t && t.toLowerCase() !== 'default title') return t;
    return '';
  }

  function itemRow(item){
    var size = sizeLine(item);
    var img  = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '';
    var many = item.quantity > 1;

    return '' +
      '<div class="bhc-item" data-key="' + esc(item.key) + '">' +
        '<div class="bhc-img">' +
          (img ? '<img src="' + esc(img) + '" alt="" width="76" height="76" loading="lazy">' : '') +
        '</div>' +
        '<div class="bhc-info">' +
          '<div class="bhc-row1">' +
            '<a class="bhc-title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
            '<span class="bhc-price">' + money(item.line_price) + '</span>' +
          '</div>' +
          (size ? '<p class="bhc-size">' + esc(size) + '</p>' : '') +
          (many ? '<p class="bhc-each">' + money(item.price) + ' each</p>' : '') +
          '<div class="bhc-qty">' +
            '<span class="bhc-stepper">' +
              /* Disabled at one rather than silently deleting the line.
                 Removing takes the button that says Remove. */
              '<button type="button" class="bhc-step" data-step="-1"' +
                (item.quantity <= 1 ? ' disabled' : '') +
                ' aria-label="Reduce quantity">&minus;</button>' +
              '<span class="bhc-n">' + item.quantity + '</span>' +
              '<button type="button" class="bhc-step" data-step="1" aria-label="Increase quantity">+</button>' +
            '</span>' +
            '<button type="button" class="bhc-remove">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  /* A tickbox, not a line item: no quantity to change and no size to check, so
     a stepper and a Remove link would dress an option up as a purchase. */
  function protCard(cart){
    if(!PROT || !realItems(cart).length) return '';
    var line = protLine(cart);
    var on   = !!line;
    return '' +
      '<label class="bhc-prot' + (on ? ' on' : '') + '"' +
             (on ? ' data-key="' + esc(line.key) + '"' : '') + '>' +
        '<input type="checkbox" class="bhc-prot-x"' + (on ? ' checked' : '') + '>' +
        '<span class="bhc-prot-t">' +
          '<b>' + esc(PROT.title) + ' <span class="bhc-prot-p">' + money(PROT.price) + '</span></b>' +
          '<small>' + esc(PROT.note || '') + '</small>' +
        '</span>' +
      '</label>';
  }

  function renderCart(cart){
    var body = el('bhc-body');
    var foot = el('bhc-foot');
    var count = document.querySelectorAll('.bhc-count');
    var label = document.querySelector('.bhc-n-items');
    if(!body) return;

    /* Boards, not cart lines. A badge reading 3 for two boards sends the
       customer into the cart to find out what the third thing is. */
    var boards = boardCount(cart);

    for(var i=0;i<count.length;i++){
      count[i].textContent = boards;
      count[i].classList.toggle('on', boards > 0);
    }

    if(label){
      label.textContent = boards === 0 ? '' : boards === 1 ? '1 item' : boards + ' items';
    }

    if(!realItems(cart).length){
      body.innerHTML = '<div class="bhc-empty">' +
        '<p class="bhc-empty-h">Nothing in your cart yet</p>' +
        '<p>Measure the bare sofa base with the cushions off, and we will match you to the right board.</p>' +
        '<a class="btn btn-primary btn-block" href="/pages/find-my-size">Find my size</a>' +
        '</div>';
      if(foot) foot.innerHTML = '';
      return;
    }

    /* Items, then the option, then the note. They came to look at their own
       order, so that goes first and everything of ours comes after it. */
    body.innerHTML =
      realItems(cart).map(itemRow).join('') +
      protCard(cart) +
      '<p class="bhc-check"><b>Worth thirty seconds.</b> Check the width and depth above ' +
      'against your own sofa with the cushions off. A matched size is what gives you firm, ' +
      'even support right across the seat. ' +
      '<a href="/pages/contact">Send us your numbers</a> and we will confirm before you pay.</p>';

    /* One action, nothing under it. "Keep looking" sat here and read as a
       second option at the moment the choosing was finished. Leaving is
       already free three ways: close, scrim, escape. */
    if(foot){
      foot.innerHTML = '' +
        '<div class="bhc-sub"><span>Subtotal</span><b>' + money(cart.total_price) + '</b></div>' +
        '<p class="bhc-ship">Free shipping across the 48 contiguous states. Taxes worked out at checkout.</p>' +
        '<a class="btn btn-primary btn-block btn-lg" href="/checkout">Check out</a>' +
        '<p class="bhc-trust">' +
          '<span>30-day returns</span><span>Lifetime warranty</span><span>Secure checkout</span>' +
        '</p>';
    }
  }

  function refresh(open){
    return getCart().then(reconcile).then(function(cart){
      renderCart(cart);
      if(open) openDrawer();
      return cart;
    });
  }

  /* ---------- feedback ---------- */

  function flash(btn, text){
    var old = btn.getAttribute('data-label') || btn.textContent;
    btn.setAttribute('data-label', old);
    btn.textContent = text;
    setTimeout(function(){ btn.textContent = btn.getAttribute('data-label'); }, 1600);
  }

  function fail(btn, message){
    var box = btn.closest('.bh-buyrow');
    if(!box) { alert(message); return; }
    var e = box.querySelector('.bh-buyerr');
    if(!e){ e = document.createElement('p'); e.className = 'bh-buyerr'; box.appendChild(e); }
    e.textContent = message;
  }

  /* ---------- upgrade the finder's result buttons ----------
     size-logic.js prints a plain <a href="/cart/ID:1"> plus a small underlined
     Details link. That file is left alone and the pair is rebuilt here.

     The main result card gets ONE button. Three of equal weight is a menu
     rather than an answer, and the second-board offer underneath is already
     asking for attention. One full-width Buy now carrying the price, then two
     quiet text links. Add to cart stays available as a link: somebody who
     wants to think it over needs somewhere to put it, and a customer with no
     way to say "not yet" closes the tab instead.

     The small cards in list views keep the three-button row. There the
     customer is comparing rather than deciding.

     The product link names its contents. "More details" tells nobody whether
     the click is for them, so it collects idle curiosity from people who were
     ready to buy and gets ignored by the one person who needed the reviews. */

  var DETAILS_LABEL = "See the reviews and what's in the box";

  function variantsFrom(href){
    var m = href.match(/\/cart\/(.+)$/);
    if(!m) return [];
    return m[1].split(',').map(function(part){
      var bits = part.split(':');
      return {id: bits[0], qty: Number(bits[1] || 1)};
    }).filter(function(v){ return /^\d+$/.test(v.id); });
  }

  function upgrade(link){
    if(link.dataset.bhDone) return;
    var list = variantsFrom(link.getAttribute('href') || '');
    if(!list.length) return;
    link.dataset.bhDone = '1';

    var many  = list.length > 1;
    var block = link.classList.contains('btn-block');
    var label = link.getAttribute('data-buy-label');

    /* THE FINDER'S ANSWER, ON ITS WAY TO THE CART.
       Three conditions together identify the one link that represents the
       match: full width, a single variant, and inside the result card. The
       small .optcard rows in "every size" fail the first two, and the second
       board offer carries two variants so it fails the middle one. */
    if(block && !many && link.closest('.result')) autoAdd(list[0].id);

    /* The product URL is already on the card as a text link. Reuse it, then
       hide the text link so the same destination is not offered twice. The
       href carries ?variant=, so the size just chosen is still selected on
       arrival and the trip costs no decisions. */
    var card = link.closest('.optcard') || link.closest('.result') || link.parentNode;
    var detailsHref = null;
    if(!many && card){
      var textLink = card.querySelector('a[href*="/products/"]');
      if(textLink){ detailsHref = textLink.getAttribute('href'); textLink.hidden = true; }
    }

    var row = document.createElement('div');
    row.className = 'bh-buyrow' + (block ? ' bh-buyrow-block' : '');

    var buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'btn btn-primary bh-buynow' + (block ? ' btn-block btn-lg' : '');
    buy.textContent = label || (many ? 'Buy both now' : 'Buy it now');

    /* Checkout uses ensure, not add: the finder may already have put this
       exact size in the cart, and Buy it now must never turn one board into
       two. It also reconciles before redirecting, because this path skips the
       drawer and is otherwise the one place protection would be missed. */
    function run(btn, checkout){
      if(busy) return;
      busy = true;
      btn.classList.add('loading');
      var work = checkout ? ensure(list) : addMany(list);
      work.then(function(){
        if(checkout){
          return getCart().then(reconcile).then(function(){ window.location.href = '/checkout'; });
        }
        busy = false;
        btn.classList.remove('loading');
        flash(btn, 'Added');
        refresh(true);
      }).catch(function(err){
        busy = false;
        btn.classList.remove('loading');
        fail(btn, err.message);
      });
    }

    buy.addEventListener('click', function(){ run(buy, true); });
    row.appendChild(buy);

    if(block){
      /* Text, not buttons: these answer "not yet" and "tell me more", and
         neither should look like a second chance to buy next to the first. */
      var alts = document.createElement('div');
      alts.className = 'bh-alts';

      if(detailsHref){
        var detLink = document.createElement('a');
        detLink.className = 'bh-altlink';
        detLink.setAttribute('href', detailsHref);
        detLink.textContent = DETAILS_LABEL;
        alts.appendChild(detLink);
      }

      var addLink = document.createElement('button');
      addLink.type = 'button';
      addLink.className = 'bh-altlink';
      addLink.textContent = many ? 'Add both to cart instead' : 'Add to cart instead';
      addLink.addEventListener('click', function(){ run(addLink, false); });
      alts.appendChild(addLink);

      row.appendChild(alts);
    } else {
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'btn btn-secondary bh-addcart';
      add.textContent = many ? 'Add both to cart' : 'Add to cart';
      add.addEventListener('click', function(){ run(add, false); });
      row.appendChild(add);

      if(detailsHref){
        var det = document.createElement('a');
        det.className = 'btn bh-details';
        det.setAttribute('href', detailsHref);
        det.textContent = 'More details';
        row.appendChild(det);
      }
    }

    link.parentNode.replaceChild(row, link);
  }

  function scan(root){
    var links = (root || document).querySelectorAll('a[href^="/cart/"]');
    for(var i=0;i<links.length;i++){
      var h = links[i].getAttribute('href');
      if(h === '/cart' || h === '/cart/') continue;
      upgrade(links[i]);
    }
  }

  /* The finder rewrites #out on every result and the second-board picker adds
     its buy link after the fact, so watch rather than scanning once. */
  function watch(){
    var out = el('out');
    if(!out) return;
    new MutationObserver(function(){ scan(out); }).observe(out, {childList:true, subtree:true});
  }

  /* ---------- product page forms ---------- */

  function wireForms(){
    document.addEventListener('submit', function(e){
      var form = e.target;
      if(!form.matches('form[action*="/cart/add"]')) return;
      if(form.dataset.bhSkip) return;
      e.preventDefault();
      var idField = form.querySelector('[name="id"]');
      if(!idField || !idField.value) return;
      /* The quantity field is read rather than assumed. It was hardcoded to 1,
         so a quantity control on the product page would have been collected
         and then thrown away. */
      var qtyField = form.querySelector('[name="quantity"]');
      var qty = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);
      var btn = form.querySelector('[type="submit"]') || form.querySelector('button');
      if(busy) return;
      busy = true;
      addMany([{id:idField.value, qty:qty}]).then(function(){
        if(form.dataset.bhCheckout === '1'){
          return getCart().then(reconcile).then(function(){ window.location.href = '/checkout'; });
        }
        busy = false;
        if(btn) flash(btn, 'Added');
        refresh(true);
      }).catch(function(err){
        busy = false;
        alert(err.message);
      });
    });
  }

  /* ---------- drawer interactions ---------- */

  function wireDrawer(){
    document.addEventListener('click', function(e){
      if(e.target.closest('.bhc-close, .bhc-scrim')){ closeDrawer(); return; }
      if(e.target.closest('.bhc-open')){ e.preventDefault(); refresh(true); return; }

      var step = e.target.closest('.bhc-step');
      if(step){
        if(step.disabled) return;
        var itemA = step.closest('.bhc-item');
        var n = Number(itemA.querySelector('.bhc-n').textContent) + Number(step.dataset.step);
        /* Touching a line by hand makes it the customer's, not the finder's,
           so the finder stops claiming the right to replace it. */
        if(lastAuto && lastAuto.key === itemA.dataset.key) lastAuto = null;
        changeLine(itemA.dataset.key, Math.max(0, n)).then(reconcile).then(renderCart);
        return;
      }

      var rm = e.target.closest('.bhc-remove');
      if(rm){
        var itemB = rm.closest('.bhc-item');
        if(lastAuto && lastAuto.key === itemB.dataset.key) lastAuto = null;
        changeLine(itemB.dataset.key, 0).then(reconcile).then(renderCart);
      }
    });

    /* The protection tickbox. Unticking records the choice for the visit so
       reconcile does not put it back on the next add and turn a decision into
       an argument. The box is disabled in flight and put back the way it was
       if the request fails, so the tick always describes the cart. */
    document.addEventListener('change', function(e){
      var box = e.target.closest('.bhc-prot-x');
      if(!box || !PROT) return;
      var card = box.closest('.bhc-prot');
      box.disabled = true;

      if(box.checked){
        setOptOut(false);
        addMany([{id: PROT.id, qty: 1}]).then(getCart).then(renderCart)
          .catch(function(){ box.disabled = false; box.checked = false; });
        return;
      }

      setOptOut(true);
      var key = card && card.dataset.key;
      if(!key){ box.disabled = false; return; }
      changeLine(key, 0).then(renderCart)
        .catch(function(){ box.disabled = false; box.checked = true; });
    });

    document.addEventListener('keydown', function(e){
      if(e.key === 'Escape') closeDrawer();
    });
  }

  function init(){
    wireDrawer();
    wireForms();
    scan(document);
    watch();
    getCart().then(reconcile).then(renderCart);
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BHCart = {open: function(){ refresh(true); }, close: closeDrawer, refresh: refresh};
})();
